<template>
  <div
    :class="className"
    v-on="othersEvent"
  >
    <!-- 前缀 -->
    <span
      v-if="hasPrefix"
      class="ten-input__prefix"
    >
      <slot name="prefix">{{ prefix }}</slot>
    </span>
    <div
      :class="inputWrapperClassName"
      @click="focus"
    >
      <!-- 前缀icon -->
      <span
        v-if="hasPrefixIcon"
        class="ten-input__input-icon ten-input__input-icon--prefix"
      >
        <slot name="prefixIcon">
          <ten-icon :icon="prefixIcon" />
        </slot>
      </span>

      <span
        v-if="this.$scopedSlots.inputPrefix"
        ref="refInputPrefix"
        class="ten-input__input-prefix"
      >
        <!-- @slot @ignore 组件使用 -->
        <slot name="inputPrefix" />
      </span>

      <span v-if="this.$scopedSlots.inputPrefixRaw">
        <!-- @slot @ignore input-tag组件使用 -->
        <slot name="inputPrefixRaw" />
      </span>

      <input
        ref="refInputElem"
        class="ten-input__input-input"
        v-bind="inputProps"
        v-on="inputEvents"
        @compositionstart="onCompositionstart"
        @compositionend="onCompositionend"
        @input="onInput"
        @change="onChange"
        @blur="onBlur"
        @focus="onFocus"
        @keydown.enter="onEnter"
      >

      <span
        v-if="this.$scopedSlots.inputSuffix"
        ref="refInputSuffix"
        class="ten-input__input-suffix"
      >
        <!-- @slot @ignore input-tag组件使用 -->
        <slot name="inputSuffix" />
      </span>

      <!-- 后缀icon -->
      <span
        v-if="hasSuffixIcon"
        class="ten-input__input-icon ten-input__input-icon--suffix"
      >
        <ten-icon
          v-if="showClear"
          class="ten-input__input-close"
          icon="circle_clear"
          @mousedown.prevent
          @click.stop="onClear"
        />
        <slot
          v-else
          name="suffixIcon"
        >
          <ten-icon
            :icon="suffixIcon"
          />
        </slot>
      </span>
    </div>
    <!-- 后缀 -->
    <span
      v-if="hasSuffix"
      class="ten-input__suffix"
    >
      <slot name="suffix">{{ suffix }}</slot>
    </span>
  </div>
</template>

<script>
import TenIcon from '../icon';
const name = 'ten-input';

export default {
  name,
  components: {
    TenIcon,
  },
  inheritAttrs: false,
  inject: {
    group: {
      default: undefined,
    },
    formItem: {
      from: 'tenFormItem',
      default: null,
    },
  },
  props: {
    /**
     * @model
     *
     * value 值
     */
    value: { type: [String, Number] },
    /**
     * 默认 value 值
     */
    defaultValue: [String, Number],
    /**
     * 前缀，支持同名slot
     */
    prefix: { type: String },
    /**
     * 后缀，支持同名slot
     */
    suffix: { type: String },
    /**
     * 前缀Icon，支持同名slot
     */
    prefixIcon: { type: String },
    /**
     * 后缀Icon，支持同名slot
     */
    suffixIcon: { type: String },
    /**
     * 原生属性
     */
    placeholder: { type: String },
    /**
     * 原生属性
     */
    name: { type: String },
    /**
     * 原生属性
     */
    type: { type: String },
    /**
     * 尺寸
     *
     * @member default | small
     */
    size: { type: String, default: 'default', validator(v) {
      return ['default', 'small'].indexOf(v) > -1;
    } },
    /**
     * 是否为错误状态
     */
    error: { type: Boolean, default: false },
    /**
     * 禁用
     */
    disabled: { type: Boolean, default: false },
    /**
     * 是否显示清除按钮
     */
    clearable: { type: Boolean, default: false },
    /**
     * 获取原生input Dom
     * @ignore
     */
    getInput: { type: Function },
  },
  data() {
    return {
      focused: false,
    };
  },
  computed: {
    hasPrefix() {
      return !!(this.$scopedSlots.prefix || this.prefix);
    },
    hasSuffix() {
      return !!(this.$scopedSlots.suffix || this.suffix);
    },
    hasPrefixIcon() {
      return !!(this.$scopedSlots.prefixIcon || this.prefixIcon);
    },
    hasSuffixIcon() {
      return !!(this.$scopedSlots.suffixIcon || this.suffixIcon || this.showClear);
    },
    showClear() {
      return this.value && !this.disabled && this.clearable;
    },
    className() {
      return [
        'ten-input',
        `ten-input--size-${this.group ? this.group.size : this.size}`,
        {
          'ten-input--status-error': this.error,
          'ten-input--disabled': this.disabled,
          'ten-input--focused': this.focused,
          'ten-input--with-prefix': this.hasPrefix,
          'ten-input--with-suffix': this.hasSuffix,
        },
      ];
    },
    inputWrapperClassName() {
      return [
        'ten-input__input',
        {
          'ten-input__input--status-error': this.error,
          'ten-input__input--focused': this.focused,
          'ten-input__input--disabled': this.disabled,
          'ten-input__input--prefixicon': this.hasPrefixIcon,
          'ten-input__input--suffixicon': this.hasSuffixIcon,
        },
      ];
    },
    inputEvents() {
      const { keydown, keyup, keypress } = this.$listeners;
      const events = { keydown, keyup, keypress };
      Object.keys(events).forEach((e) => {
        if (!events[e]) delete events[e];
      });
      return events;
    },
    inputProps() {
      return {
        ...this.$attrs,
        name: this.name,
        placeholder: this.placeholder,
        disabled: this.disabled,
        clearable: this.clearable,
        type: this.type || 'text',
      };
    },
    othersEvent() {
      const {
        keydown,
        keyup,
        keypress,
        change,
        input,
        focus,
        blur,
        ...others
      } = this.$listeners;

      return others;
    },
  },
  watch: {
    value: {
      handler(v) {
        if (this.$refs.refInputElem) {
          this.setInputValue(v);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.setInputValue(this.value);
    this.getInput && this.getInput(this.$refs.refInputElem);
  },
  created() {
    this.blurT = null;
  },
  beforeDestroy() {
    clearTimeout(this.blurT);
  },
  methods: {
    setInputValue(v = '') {
      const input = this.$refs.refInputElem;
      const sV = String(v);
      if (!input) {
        return;
      }
      if (input.value !== sV) {
        input.value = sV;
      }
    },
    onCompositionstart() {
      this.isComposing = true;
    },
    onCompositionend(e) {
      if (this.isComposing) {
        this.isComposing = false;
        this.onInput(e);
      }
    },
    onChange(e) {
      /**
       * change 回调
       *
       * @param {Object} e 事件对象
       */
      this.$emit('change', e);
    },
    onInput(e) {
      if (this.isComposing) return;
      /**
       * input 回调
       *
       * @param {String} value 输入值
       */
      this.$emit('input', e.target.value);
      this.$nextTick(() => {
        this.formItem && this.formItem.$emit('form.change');
      });
      // 受控
      this.$nextTick(() => this.setInputValue(this.value));
    },
    onFocus(e) {
      clearTimeout(this.blurT);
      this.focused = true;
      this.readyBlur = false;
      /**
       * 聚焦回调
       *
       * @param {Object} e 事件对象
       */
      this.$emit('focus', e);
    },
    onBlur(e) {
      this.readyBlur = true;
      this.blurT = setTimeout(() => {
        if (this.readyBlur && this.focused) {
          this.focused = false;
        }
      }, 150);
      /**
       * 失焦回调
       *
       * @param {Object} e 事件对象
       */
      this.$emit('blur', e);
      this.formItem && this.formItem.$emit('form.blur');
    },
    onClear() {
      /**
       * 点击清空按钮后触发
       */
      this.$emit('clear');
      this.$emit('input', '');
    },
    onEnter(e) {
      if (this.isComposing) return;
      /**
       *
       * 按下 enter 确认键后的回调事件
       * @param {Object} e 事件对象
       */
      this.$emit('enter', e);
    },
    /**
     * @public
     *
     * 使输入框获得焦点, 支持使用 v-focus 指令
     * @param {boolean}
     */
    focus() {
      this.$refs.refInputElem && this.$refs.refInputElem.focus();
    },
    /**
     * @public
     *
     * 使输入框获失去焦点
     */
    blur() {
      this.$refs.refInputElem && this.$refs.refInputElem.blur();
    },

    /**
     * @public
     *
     * 选中输入框文本
     */
    select() {
      this.$refs.refInputElem && this.$refs.refInputElem.select();
    }
  },
};
</script>
